import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fileUrl } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, MapPin, ArrowLeft, Store, ShieldCheck, Truck, ArrowRight, Check } from "lucide-react";

function RequestWholesaleForm({ product, supplier }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    volume: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const supplierLabel = supplier?.company_name || supplier?.name || "supplier";
      const contextMessage = [
        `Wholesale quote request — ${product?.name} (${supplierLabel})`,
        f.volume ? `Target volume: ${f.volume}` : null,
        f.message ? `Notes: ${f.message}` : null,
      ].filter(Boolean).join("\n");
      await api.post("/leads", {
        name: f.name,
        email: f.email,
        company: f.company || undefined,
        country: f.country || undefined,
        interest: `wholesale_quote:${supplier?.id || ""}:${product?.id || ""}`,
        message: contextMessage,
      });
      setDone(true);
      toast.success(t("productPublic.formSuccess"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="wholesale-success"
        className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-secondary mb-4">
          <Check className="w-6 h-6" />
        </div>
        <div className="font-heading text-lg font-black text-secondary">
          {t("productPublic.successTitle")}
        </div>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
          {t("productPublic.successBody")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="wholesale-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>{t("productPublic.name")}</Label>
          <Input
            data-testid="wholesale-name"
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </div>
        <div>
          <Label>{t("productPublic.email")}</Label>
          <Input
            data-testid="wholesale-email"
            type="email"
            required
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>{t("productPublic.company")}</Label>
          <Input
            data-testid="wholesale-company"
            value={f.company}
            onChange={(e) => setF({ ...f, company: e.target.value })}
          />
        </div>
        <div>
          <Label>{t("productPublic.country")}</Label>
          <Input
            data-testid="wholesale-country"
            value={f.country}
            onChange={(e) => setF({ ...f, country: e.target.value })}
          />
        </div>
      </div>
      <div>
        <Label>{t("productPublic.volume")}</Label>
        <Input
          data-testid="wholesale-volume"
          value={f.volume}
          onChange={(e) => setF({ ...f, volume: e.target.value })}
          placeholder={t("productPublic.volumePlaceholder")}
        />
      </div>
      <div>
        <Label>{t("productPublic.notes")}</Label>
        <Textarea
          data-testid="wholesale-notes"
          rows={4}
          value={f.message}
          onChange={(e) => setF({ ...f, message: e.target.value })}
          placeholder={t("productPublic.notesPlaceholder")}
        />
      </div>
      <Button
        type="submit"
        data-testid="wholesale-submit"
        size="lg"
        className="w-full h-12"
        disabled={busy}
      >
        {busy ? t("productPublic.submitting") : t("productPublic.submit")}
        {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
      </Button>
      <p className="text-xs text-muted-foreground text-center leading-relaxed">
        {t("productPublic.terms")}
      </p>
    </form>
  );
}

export default function SupplierProductPublic() {
  const { pid } = useParams();
  const { t } = useTranslation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/products/${pid}`)
      .then((r) => setProduct(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "notFound" : "error"))
      .finally(() => setLoading(false));
  }, [pid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="max-w-5xl mx-auto px-6 py-24 text-muted-foreground text-center" data-testid="product-loading">
          {t("productPublic.loading")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="max-w-5xl mx-auto px-6 py-24 text-center">
          <div className="font-heading text-2xl font-black text-secondary" data-testid="product-not-found">
            {t("productPublic.notFound")}
          </div>
          <Link to="/suppliers" className="inline-flex items-center gap-1.5 mt-4 text-primary hover:underline">
            <ArrowLeft className="w-4 h-4" /> {t("productPublic.backToDirectory")}
          </Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const supplier = product.supplier || {};
  const supplierLabel = supplier.company_name || supplier.name || t("productPublic.supplier");
  const gallery = [
    ...(product.image_urls || []),
    ...(product.image_ids || []).map((id) => fileUrl(id)),
  ];
  const hasImages = gallery.length > 0;

  return (
    <div className="min-h-screen bg-background" data-testid="product-public-page">
      <PublicHeader />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        {/* Breadcrumb */}
        <div className="mb-6 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
          <Link to="/suppliers" className="hover:text-primary">
            {t("productPublic.breadDirectory")}
          </Link>
          <span>·</span>
          {product.supplier_id && (
            <>
              <Link to={`/suppliers/${product.supplier_id}`} className="hover:text-primary">
                {supplierLabel}
              </Link>
              <span>·</span>
            </>
          )}
          <span className="text-secondary font-medium">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10">
          {/* Left: images + product info */}
          <div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted grid place-items-center border border-border">
              {hasImages ? (
                <img
                  src={gallery[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Package className="w-16 h-16 text-muted-foreground" />
              )}
            </div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {gallery.slice(1, 5).map((src, i) => (
                  <div key={i} className="aspect-square rounded-md overflow-hidden bg-muted border border-border">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8">
              <div className="overline text-primary">{product.category}</div>
              <h1
                className="mt-2 font-heading text-3xl md:text-4xl font-black tracking-tight text-secondary leading-tight"
                data-testid="product-name"
              >
                {product.name}
              </h1>
              {product.description && (
                <p className="mt-4 text-base text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {product.description}
                </p>
              )}

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="rounded-md border border-border p-4">
                  <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                    {t("productPublic.moq")}
                  </div>
                  <div className="mt-1 text-secondary font-heading font-bold">
                    {product.moq || "—"} {product.unit || ""}
                  </div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="text-[10px] font-heading font-black uppercase tracking-widest text-muted-foreground">
                    {t("productPublic.priceRange")}
                  </div>
                  <div className="mt-1 text-secondary font-heading font-bold">
                    {product.price_min
                      ? `${product.price_min}${product.price_max ? `–${product.price_max}` : ""} ${product.currency || ""}/${product.unit || ""}`
                      : t("productPublic.onRequest")}
                  </div>
                </div>
              </div>

              {supplier.company_name && (
                <Card className="mt-6" data-testid="product-supplier-card">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-md bg-secondary text-primary grid place-items-center">
                      <Store className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="overline text-primary text-[10px]">{t("productPublic.soldBy")}</div>
                      <div className="font-heading font-bold text-secondary truncate">{supplierLabel}</div>
                      {supplier.country && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" /> {supplier.country}
                        </div>
                      )}
                    </div>
                    {product.supplier_id && (
                      <Link to={`/suppliers/${product.supplier_id}`}>
                        <Button variant="outline" size="sm" data-testid="visit-supplier-shop">
                          {t("productPublic.visitShop")}
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-md border border-border/70 bg-secondary/[0.03] p-4">
                  <ShieldCheck className="w-5 h-5 text-primary mb-2" />
                  <div className="text-sm font-heading font-bold text-secondary">
                    {t("productPublic.trustA")}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t("productPublic.trustAsub")}
                  </p>
                </div>
                <div className="rounded-md border border-border/70 bg-secondary/[0.03] p-4">
                  <Truck className="w-5 h-5 text-primary mb-2" />
                  <div className="text-sm font-heading font-bold text-secondary">
                    {t("productPublic.trustB")}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t("productPublic.trustBsub")}
                  </p>
                </div>
                <div className="rounded-md border border-border/70 bg-secondary/[0.03] p-4">
                  <Package className="w-5 h-5 text-primary mb-2" />
                  <div className="text-sm font-heading font-bold text-secondary">
                    {t("productPublic.trustC")}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t("productPublic.trustCsub")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: request wholesale form */}
          <div>
            <div className="sticky top-24 rounded-xl border border-primary/25 bg-white p-6 md:p-8 shadow-lg">
              <Badge className="mb-3">{t("productPublic.wholesaleBadge")}</Badge>
              <h2 className="font-heading text-2xl font-black tracking-tight text-secondary">
                {t("productPublic.wholesaleH")}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {t("productPublic.wholesaleSub")}
              </p>
              <div className="mt-6">
                <RequestWholesaleForm product={product} supplier={supplier} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
